/**
 * Accept-time apply for pursuit (capture project) writes proposed by
 * propose-delivery-action. Each runs under the user's JWT through the same
 * RPCs the Pursuit pages call, so the pursuit's own access rules hold.
 * propose-delivery-action already read the pursuit and filled every field
 * the write replaces (profile, task, teaming), so params are sent as-is.
 * Keep in lockstep with services/shanks/pkg/tools/delivery_action_registry_capture.go.
 */ import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import { projectDataClient } from '../../../lib/connect/clients';
import { rehostChatFile } from './applyDeliveryUpload';
import { resolveChatFiles } from './assistChatFiles';
import { receiptHrefForAction } from './deliveryActionDestinations';
export const CAPTURE_ACTIONS = [
    'log_capture_interaction',
    'record_capture_intel',
    'link_capture_competitor',
    'link_capture_teaming_partner',
    'upsert_capture_task',
    'update_capture_profile',
    'log_rfi_event'
];
export function isCaptureAction(action) {
    return CAPTURE_ACTIONS.includes(action);
}
/**
 * Free-text RFI event fields: param name → UpsertRfiEvent field. Adding a
 * field is one line here and one in rfiEventTextFields (Go).
 */ export const RFI_EVENT_TEXT_FIELDS = [
    {
        param: 'source_notice_id',
        key: 'sourceNoticeId'
    },
    {
        param: 'questions',
        key: 'questions'
    },
    {
        param: 'notes',
        key: 'notes'
    },
    {
        param: 'response_reason',
        key: 'responseReason'
    }
];
/** Capture profile fields: param name → UpsertCaptureProfile field. */ const PROFILE_FIELDS = [
    {
        param: 'solicitation_number',
        key: 'solicitationNumber'
    },
    {
        param: 'crm_number',
        key: 'crmNumber'
    },
    {
        param: 'customer_agency_names',
        key: 'customerAgencyNames'
    },
    {
        param: 'bid_position',
        key: 'bidPosition'
    },
    {
        param: 'vehicle',
        key: 'vehicle'
    },
    {
        param: 'procurement_type',
        key: 'procurementType'
    },
    {
        param: 'contract_type',
        key: 'contractType'
    },
    {
        param: 'period_of_performance',
        key: 'periodOfPerformance'
    },
    {
        param: 'total_program_value',
        key: 'totalProgramValue'
    },
    {
        param: 'stage_label',
        key: 'stageLabel'
    },
    {
        param: 'pwin_percent',
        key: 'pwinPercent'
    },
    {
        param: 'risks',
        key: 'risks'
    },
    {
        param: 'capture_manager_id',
        key: 'captureManagerId'
    },
    {
        param: 'business_developer_id',
        key: 'businessDeveloperId'
    }
];
function str(params, key) {
    const v = params[key];
    return typeof v === 'string' ? v : '';
}
function bool(params, key) {
    return params[key] === true;
}
/** A YYYY-MM-DD date at noon UTC, so it reads as the same day everywhere. */ export function captureDate(ymd) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return undefined;
    const d = new Date(`${ymd}T12:00:00Z`);
    return Number.isNaN(d.getTime()) ? undefined : Timestamp.fromDate(d);
}
function fields(params, list) {
    return Object.fromEntries(list.map((f)=>[
            f.key,
            str(params, f.param)
        ]));
}
/** Plain words for the errors a pursuit write can hit. */ function plainCaptureError(err) {
    if (err instanceof ConnectError) {
        if (err.code === Code.PermissionDenied) {
            return new Error("You can't change this pursuit. Its capture manager or a pursuit member with edit rights can.");
        }
        if (err.code === Code.NotFound) {
            return new Error("That pursuit or record wasn't found, or it isn't shared with you.");
        }
    }
    return err;
}
/** Build and send one pursuit write. Returns the new record id and a receipt link. */ export async function applyCaptureAction(action, params, options) {
    const captureProjectId = str(params, 'capture_project_id');
    if (!captureProjectId) throw new Error('capture_project_id is required');
    const done = (id)=>{
        const href = receiptHrefForAction(action, {
            captureProjectId
        });
        return {
            ...id ? {
                id
            } : {},
            ...href ? {
                href
            } : {}
        };
    };
    try {
        switch(action){
            case 'log_capture_interaction':
                {
                    const resp = await projectDataClient.upsertCustomerInteraction({
                        captureProjectId,
                        id: '',
                        interactionDate: captureDate(str(params, 'interaction_date')),
                        channel: str(params, 'channel') || 'other',
                        stakeholder: str(params, 'stakeholder'),
                        summary: str(params, 'summary'),
                        title: str(params, 'title') || undefined,
                        sentiment: str(params, 'sentiment') || 'unknown',
                        followUps: str(params, 'follow_ups'),
                        notes: str(params, 'notes')
                    });
                    return done(resp.interaction?.id);
                }
            case 'record_capture_intel':
                {
                    let participantId = str(params, 'participant_id');
                    if (!participantId) {
                        // The company joins the competitive field first, as on the page.
                        const added = await projectDataClient.upsertCompetitiveFieldParticipant({
                            id: '',
                            captureProjectId,
                            companyProfileId: str(params, 'company_profile_id'),
                            inclusionBasis: str(params, 'inclusion_basis') || 'market_signal',
                            basisNote: ''
                        });
                        participantId = added.participant?.id ?? '';
                        if (!participantId) throw new Error('The company could not be added to the competitive field');
                    }
                    const resp = await projectDataClient.recordCompetitorEvidence({
                        captureProjectId,
                        participantId,
                        sourceKind: str(params, 'source_kind') || 'manual_entry',
                        sourceRef: str(params, 'source_ref'),
                        sourceDate: captureDate(str(params, 'source_date')),
                        claimType: str(params, 'claim_type'),
                        stance: str(params, 'stance') || 'supports',
                        confidence: str(params, 'confidence') || 'medium',
                        rationale: str(params, 'rationale'),
                        inferenceAssumptions: str(params, 'inference_assumptions'),
                        supersedesEvidenceId: '',
                        basis: str(params, 'basis')
                    });
                    return done(resp.evidence?.id);
                }
            case 'link_capture_competitor':
                {
                    const resp = await projectDataClient.upsertCaptureCompetitionLink({
                        captureProjectId,
                        id: str(params, 'id'),
                        companyProfileId: str(params, 'company_profile_id'),
                        role: str(params, 'role') || 'prime'
                    });
                    return done(resp.link?.id);
                }
            case 'link_capture_teaming_partner':
                {
                    const resp = await projectDataClient.upsertCaptureTeamingLink({
                        captureProjectId,
                        id: str(params, 'id'),
                        companyProfileId: str(params, 'company_profile_id'),
                        workshare: str(params, 'workshare'),
                        nda: bool(params, 'nda'),
                        ta: bool(params, 'ta'),
                        rationale: str(params, 'rationale')
                    });
                    return done(resp.link?.id);
                }
            case 'upsert_capture_task':
                {
                    const sort = params.sort_order;
                    const resp = await projectDataClient.upsertCaptureTask({
                        captureProjectId,
                        id: str(params, 'id'),
                        title: str(params, 'title'),
                        ownerUserId: str(params, 'owner_user_id'),
                        dueAt: captureDate(str(params, 'due_date')),
                        status: str(params, 'status') || 'open',
                        notes: str(params, 'notes'),
                        sortOrder: typeof sort === 'number' ? sort : 0
                    });
                    return done(resp.task?.id);
                }
            case 'update_capture_profile':
                {
                    const resp = await projectDataClient.upsertCaptureProfile({
                        captureProjectId,
                        id: str(params, 'id'),
                        ...fields(params, PROFILE_FIELDS)
                    });
                    return done(resp.profile?.id);
                }
            case 'log_rfi_event':
                {
                    let responseArtifactId = str(params, 'response_artifact_id');
                    const files = await resolveChatFiles(params);
                    if (files.length > 1) throw new Error('An RFI event takes one response file');
                    if (files.length === 1) {
                        // A chat upload or a file the assistant made is copied out of the
                        // chat, so the response outlives it.
                        if (!options?.tenantId || !options.ownerUserId) {
                            throw new Error('Sign in is required to file this upload.');
                        }
                        responseArtifactId = await rehostChatFile(files[0], {
                            tenantId: options.tenantId,
                            ownerUserId: options.ownerUserId
                        });
                    }
                    const resp = await projectDataClient.upsertRfiEvent({
                        captureProjectId,
                        id: '',
                        title: str(params, 'title'),
                        rfiType: str(params, 'rfi_type') || 'rfi',
                        eventDate: captureDate(str(params, 'event_date')),
                        responseDue: captureDate(str(params, 'response_due')),
                        responseArtifactId,
                        responseStatus: str(params, 'response_status'),
                        impactedRequirementsJson: '',
                        impactedVehicleStrategy: '',
                        ...fields(params, RFI_EVENT_TEXT_FIELDS)
                    });
                    const rfiResponseProjectId = str(params, 'rfi_response_project_id');
                    if (rfiResponseProjectId) {
                        await projectDataClient.linkRfiResponseToCapture({
                            captureProjectId,
                            rfiResponseProjectId
                        });
                    }
                    return done(resp.rfiEvent?.id);
                }
            default:
                throw new Error(`Unknown pursuit action ${action}`);
        }
    } catch (err) {
        throw plainCaptureError(err);
    }
}
